import React, { useState } from 'react';
import { CircleCheck as CheckCircle, Calendar, User, DollarSign } from 'lucide-react';
import type { RegistrationLink } from './types';
import { formatCurrency } from './types';

interface Props {
  link: RegistrationLink;
  onConfirm: () => void;
}

export default function SimulationReviewStep({ link, onConfirm }: Props) {
  const [confirmed, setConfirmed] = useState(false);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Condições do Empréstimo</h2>
        <p className="text-gray-600 text-sm">
          Revise atentamente os dados abaixo antes de continuar o cadastro.
        </p>
      </div>

      {/* Seller info */}
      <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-4">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <p className="text-xs text-gray-500">Vendedora responsável</p>
          <p className="font-semibold text-gray-900">{link.seller_name || 'Vendedora'}</p>
        </div>
      </div>

      {/* Main values */}
      <div className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">Resumo financeiro</span>
          </div>
        </div>
        <div className="divide-y divide-gray-50">
          <div className="flex justify-between items-center px-4 py-3">
            <span className="text-sm text-gray-600">Valor do empréstimo</span>
            <span className="font-semibold text-gray-900">{formatCurrency(link.loan_amount)}</span>
          </div>
          <div className="flex justify-between items-center px-4 py-3">
            <span className="text-sm text-gray-600">Taxa de juros</span>
            <span className="font-semibold text-gray-900">{link.interest_rate}%</span>
          </div>
          <div className="flex justify-between items-center px-4 py-3">
            <span className="text-sm text-gray-600">Número de parcelas</span>
            <span className="font-semibold text-gray-900">{link.installments}x</span>
          </div>
          <div className="flex justify-between items-center px-4 py-3">
            <span className="text-sm text-gray-600">Valor por parcela</span>
            <span className="font-semibold text-gray-900">{formatCurrency(link.installment_amount)}</span>
          </div>
          <div className="flex justify-between items-center px-4 py-3 bg-amber-50">
            <span className="text-sm font-semibold text-gray-800">Total a pagar</span>
            <span className="text-lg font-bold text-amber-700">{formatCurrency(link.total_amount)}</span>
          </div>
        </div>
      </div>

      {/* Installment dates */}
      {link.installment_dates && link.installment_dates.length > 0 && (
        <div className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-700">Datas de vencimento</span>
            </div>
          </div>
          <div className="divide-y divide-gray-50">
            {link.installment_dates.map((date, i) => (
              <div key={i} className="flex justify-between items-center px-4 py-3">
                <span className="text-sm text-gray-600">Parcela {i + 1}</span>
                <span className="font-medium text-gray-900">
                  {new Date(date + 'T12:00:00').toLocaleDateString('pt-BR')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Observations */}
      {link.observations && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <p className="text-xs font-semibold text-blue-700 mb-1 uppercase tracking-wide">Observações da vendedora</p>
          <p className="text-sm text-blue-900">{link.observations}</p>
        </div>
      )}

      {/* Confirmation */}
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 cursor-pointer flex-shrink-0"
        />
        <span className="text-sm text-gray-700">
          Entendi as condições do empréstimo e desejo prosseguir com o cadastro.
        </span>
      </label>

      <button
        onClick={onConfirm}
        disabled={!confirmed}
        className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        <CheckCircle className="w-5 h-5" />
        Confirmar e Continuar
      </button>
    </div>
  );
}
